// src/pages/SuperAdmin/components/FinancialDashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Timeline,
  ThumbUp,
  People,
  CardMembership,
  Flight,
  MonetizationOn,
  ContactMail
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import SubscriptionSummary from './SubscriptionSummary';
import TripRevenueTable from './TripRevenueTable';
import UserPaymentStatus from './UserPaymentStatus';
import PaymentTrends from './PaymentTrends';

// Interface for user payment data
interface UserPaymentData {
  id: string;
  email: string;
  displayName: string;
  hasSubscription: boolean;
  subscriptionStatus?: string;
  subscriptionStart?: Date;
  subscriptionEnd?: Date;
  amountPaid?: number;
  lastPaymentDate?: Date;
  paidTrips: number;
  registrationType?: 'general' | 'paid';
  inviteType?: 'general' | 'paid';
  wasInvited?: boolean;
}

// Interface for revenue data
interface RevenueData {
  totalRevenue: number;
  subscriptionRevenue: number;
  tripRevenue: number;
  activeSubscribers: number;
  totalSubscribers: number;
  paidTrips: number;
  generalUsers: number;
  paidUsers: number;
  generalInviteUsers: number;
  paidInviteUsers: number;
  totalUsers: number;
  totalInvitations: number;
  acceptedInvitations: number;
  pendingInvitations: number;
  invitationConversionRate: number;
  monthlySummary: {
    month: string;
    subscriptionRevenue: number;
    tripRevenue: number;
    totalRevenue: number;
  }[];
}

// Interface for secondary metric in MetricCard
interface SecondaryMetric {
  label: string;
  value: string;
  percent: number;
}

const FinancialDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [userPayments, setUserPayments] = useState<UserPaymentData[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all financial data
  const fetchFinancialData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch user subscription data
      await fetchUserPaymentData();
      
      // Fetch revenue summary
      await fetchRevenueSummary();
    } catch (err: any) {
      console.error('Error fetching financial data:', err);
      setError('Failed to load financial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user payment data
  const fetchUserPaymentData = async () => {
    try {
      // Query all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const userData: UserPaymentData[] = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const user = userDoc.data();
        
        // Query trip payments for this user
        const paymentsQuery = query(
          collection(db, 'tripPayments'),
          where('userId', '==', userDoc.id)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        
        // Calculate total amount paid for trips
        let totalPaid = 0;
        let lastPaymentDate: Date | null = null;
        
        paymentsSnapshot.docs.forEach(doc => {
          const payment = doc.data();
          totalPaid += payment.amount || 0;
          
          // Track most recent payment date
          if (payment.timestamp) {
            const paymentDate = payment.timestamp.toDate();
            if (!lastPaymentDate || paymentDate > lastPaymentDate) {
              lastPaymentDate = paymentDate;
            }
          }
        });
        
        // Check if user has created any trips
        const tripsQuery = query(
          collection(db, 'trips'),
          where('createdBy', '==', userDoc.id)
        );
        const tripsSnapshot = await getDocs(tripsQuery);
        const hasCreatedTrips = tripsSnapshot.size > 0;
        
        // Check if user was invited to any trips
        const invitationsQuery = query(
          collection(db, 'tripParticipants'),
          where('userId', '==', userDoc.id),
          where('status', '==', 'accepted')
        );
        const invitationsSnapshot = await getDocs(invitationsQuery);
        const wasInvited = invitationsSnapshot.size > 0;
        
        // Determine user registration type
        const registrationType = paymentsSnapshot.size > 0 || user.subscription?.active 
          ? 'paid' as const
          : 'general' as const;
        
        // Determine invite type
        let inviteType: 'general' | 'paid' | undefined = undefined;
        if (wasInvited) {
          inviteType = hasCreatedTrips && (paymentsSnapshot.size > 0 || user.subscription?.active)
            ? 'paid'
            : 'general';
        }
        
        userData.push({
          id: userDoc.id,
          email: user.email || '',
          displayName: user.displayName || '',
          hasSubscription: user.subscription?.active || false,
          subscriptionStatus: user.subscription?.status,
          subscriptionStart: user.subscription?.createdAt ? new Date(user.subscription.createdAt) : undefined,
          subscriptionEnd: user.subscription?.currentPeriodEnd 
            ? new Date(user.subscription.currentPeriodEnd) 
            : undefined,
          amountPaid: totalPaid,
          lastPaymentDate: lastPaymentDate || undefined,
          paidTrips: paymentsSnapshot.docs.length,
          registrationType,
          inviteType,
          wasInvited
        });
      }
      
      setUserPayments(userData);
    } catch (err) {
      console.error('Error fetching user payment data:', err);
      throw new Error('Failed to load user payment data');
    }
  };

  // Fetch revenue summary
  const fetchRevenueSummary = async () => {
    try {
      // Query all payments 
      const paymentsRef = collection(db, 'tripPayments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      
      // Query all subscriptions
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // Query invitations - using tripInvitations collection
      const invitationsRef = collection(db, 'tripInvitations');
      const invitationsSnapshot = await getDocs(invitationsRef);
      const totalInvitations = invitationsSnapshot.size;
      
      // Get accepted invitations
      const acceptedInvitationsQuery = query(
        collection(db, 'tripInvitations'),
        where('status', '==', 'accepted')
      );
      const acceptedInvitationsSnapshot = await getDocs(acceptedInvitationsQuery);
      const acceptedInvitations = acceptedInvitationsSnapshot.size;
      
      // Calculate pending invitations
      const pendingInvitations = totalInvitations - acceptedInvitations;
      
      // Calculate invitation conversion rate
      const invitationConversionRate = totalInvitations > 0 
        ? Math.round((acceptedInvitations / totalInvitations) * 100) 
        : 0;
      
      let totalTripRevenue = 0;
      let paidTrips = 0;
      const paidTripsByMonth: Record<string, number> = {};
      
      // Process trip payments
      paymentsSnapshot.docs.forEach(doc => {
        const payment = doc.data();
        const amount = payment.amount || 0;
        totalTripRevenue += amount;
        paidTrips++;
        
        // Group by month for trends
        if (payment.timestamp) {
          const date = payment.timestamp.toDate();
          const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
          paidTripsByMonth[monthKey] = (paidTripsByMonth[monthKey] || 0) + amount;
        }
      });
      
      // Process subscriptions
      let totalSubscriptionRevenue = 0;
      let activeSubscribers = 0;
      let totalSubscribers = 0;
      const subscriptionsByMonth: Record<string, number> = {};
      
      // Calculate user type counts - now fetch directly from users collection
      let generalUsers = 0;
      let paidUsers = 0;
      
      usersSnapshot.docs.forEach(doc => {
        const user = doc.data();
        
        // Count general vs paid users
        if (user.subscription && user.subscription.active) {
          paidUsers++;
        } else {
          generalUsers++;
        }
        
        if (user.subscription) {
          totalSubscribers++;
          
          if (user.subscription.active) {
            activeSubscribers++;
            // Assuming $25 per active subscription as per the code found
            totalSubscriptionRevenue += 25;
          }
          
          // Group by month for trends
          if (user.subscription.createdAt) {
            const date = new Date(user.subscription.createdAt);
            const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
            subscriptionsByMonth[monthKey] = (subscriptionsByMonth[monthKey] || 0) + 25;
          }
        }
      });
      
      // Calculate total users (combine general and paid)
      const totalUsers = generalUsers + paidUsers;
      
      // Get invited users count from tripInvitations
      const generalInviteUsers = userPayments.filter(user => user.inviteType === 'general').length;
      const paidInviteUsers = userPayments.filter(user => user.inviteType === 'paid').length;
      
      // Create monthly summary for the revenue chart
      const allMonths = new Set([
        ...Object.keys(paidTripsByMonth),
        ...Object.keys(subscriptionsByMonth)
      ]);
      
      const monthlySummary = Array.from(allMonths).map(monthKey => {
        const [year, month] = monthKey.split('-').map(Number);
        const date = new Date(year, month - 1, 1);
        
        const subscriptionRevenue = subscriptionsByMonth[monthKey] || 0;
        const tripRevenue = paidTripsByMonth[monthKey] || 0;
        
        return {
          month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          subscriptionRevenue,
          tripRevenue,
          totalRevenue: subscriptionRevenue + tripRevenue
        };
      }).sort((a, b) => {
        // Sort by date
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA.getTime() - dateB.getTime();
      });
      
      setRevenueData({
        totalRevenue: totalSubscriptionRevenue + totalTripRevenue,
        subscriptionRevenue: totalSubscriptionRevenue,
        tripRevenue: totalTripRevenue,
        activeSubscribers,
        totalSubscribers,
        paidTrips,
        generalUsers,
        paidUsers,
        totalUsers,
        generalInviteUsers,
        paidInviteUsers,
        totalInvitations,
        acceptedInvitations,
        pendingInvitations,
        invitationConversionRate,
        monthlySummary
      });
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      throw new Error('Failed to load revenue data');
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data on component mount
  useEffect(() => {
    fetchFinancialData();
  }, []);

  // Metric Card component for summary statistics
  const MetricCard = ({ title, value, icon, color, percentChange, secondaryMetric }: any) => {
    return (
      <Card elevation={2}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 1, 
              bgcolor: `${color}15`, 
              display: 'flex',
              mr: 2 
            }}>
              {icon}
            </Box>
            <Typography variant="h6" component="div" color="text.secondary">
              {title}
            </Typography>
          </Box>
          
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {value}
          </Typography>
          
          {percentChange && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: percentChange >= 0 ? 'success.main' : 'error.main', 
                mr: 1
              }}>
                {percentChange >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  {Math.abs(percentChange)}%
                </Typography>
              </Box>
              
              {secondaryMetric && (
                <Typography variant="body2" color="text.secondary">
                  {secondaryMetric.label}: {secondaryMetric.value}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Create a new component for user category metrics
  const UserCategoryMetrics = () => {
    if (!revenueData) return null;
    
    const totalUsers = revenueData.generalUsers + revenueData.paidUsers;
    const paidUserPercentage = totalUsers > 0
      ? Math.round((revenueData.paidUsers / totalUsers) * 100)
      : 0;
    
    const totalInviteUsers = revenueData.generalInviteUsers + revenueData.paidInviteUsers;
    const paidInvitePercentage = totalInviteUsers > 0
      ? Math.round((revenueData.paidInviteUsers / totalInviteUsers) * 100)
      : 0;
    
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>User Categories</Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Registration Types</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">General Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{revenueData.generalUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{revenueData.paidUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{totalUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid User Percentage:</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {paidUserPercentage}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={paidUserPercentage} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Invite Types</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">General Invite Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{revenueData.generalInviteUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid Invite Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{revenueData.paidInviteUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total Invite Users:</Typography>
                <Typography variant="body2" fontWeight="bold">{totalInviteUsers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Paid Invite Percentage:</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {paidInvitePercentage}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={paidInvitePercentage} 
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Card>
          </Grid>
          
          {/* Add new card for invitation metrics */}
          <Grid item xs={12}>
            <Card sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Invitation Metrics</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Invitations:</Typography>
                    <Typography variant="body2" fontWeight="bold">{revenueData.totalInvitations}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Accepted Invitations:</Typography>
                    <Typography variant="body2" fontWeight="bold">{revenueData.acceptedInvitations}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Pending Invitations:</Typography>
                    <Typography variant="body2" fontWeight="bold">{revenueData.pendingInvitations}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Conversion Rate</Typography>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {revenueData.invitationConversionRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        of invitations accepted
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={revenueData.invitationConversionRate} 
                      sx={{ height: 8, borderRadius: 1, mt: 1 }}
                      color="secondary"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Render overview tab content
  const renderOverviewTab = () => (
    <Box sx={{ mt: 3 }}>
      {revenueData && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Revenue"
                value={`$${revenueData.totalRevenue.toFixed(2)}`}
                icon={<MonetizationOn />}
                color="#4285F4"
                percentChange={10}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Subscription Revenue"
                value={`$${revenueData.subscriptionRevenue.toFixed(2)}`}
                icon={<CardMembership />}
                color="#0F9D58"
                percentChange={3}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Trip Revenue"
                value={`$${revenueData.tripRevenue.toFixed(2)}`}
                icon={<Flight />}
                color="#F4B400"
                percentChange={-3}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Active Subscribers"
                value={revenueData.activeSubscribers}
                icon={<People />}
                color="#DB4437"
                percentChange={8}
                secondaryMetric={{
                  label: "of total users",
                  value: `${revenueData.activeSubscribers}/${revenueData.totalUsers || 0}`,
                  percent: revenueData.totalUsers ? (revenueData.activeSubscribers / revenueData.totalUsers) * 100 : 0
                }}
              />
            </Grid>
            
            {/* Add a dedicated Invitation Metrics card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Total Invitations
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        {revenueData.totalInvitations}
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 42, 
                        height: 42, 
                        bgcolor: '#9c27b015', 
                        borderRadius: '50%',
                        color: '#9c27b0'
                      }}
                    >
                      <ContactMail />
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Invitation Conversion
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {revenueData.acceptedInvitations}/{revenueData.totalInvitations}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={revenueData.invitationConversionRate} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 1,
                        bgcolor: '#9c27b015',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#9c27b0'
                        }
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <UserCategoryMetrics />
          
          {/* Wrap the PaymentTrends in a Paper with margin-top styling */}
          <Paper sx={{ p: 2, mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Revenue Trends</Typography>
            <PaymentTrends 
              data={revenueData.monthlySummary}
            />
          </Paper>
        </>
      )}
    </Box>
  );

  // Render user payments tab content
  const renderUserPaymentsTab = () => (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>User Payment Status</Typography>
        <UserPaymentStatus users={userPayments} />
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Financial Dashboard
        </Typography>
        
        <IconButton 
          onClick={fetchFinancialData} 
          disabled={loading}
          color="primary"
        >
          <Refresh />
        </IconButton>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Overview" />
              <Tab label="User Payments" />
            </Tabs>
          </Paper>
          
          {tabValue === 0 && renderOverviewTab()}
          {tabValue === 1 && renderUserPaymentsTab()}
        </>
      )}
    </Box>
  );
};

export default FinancialDashboard; 